// src/config/roles.ts

/**
 * Role Configuration
 * 
 * This file defines the role hierarchy and permissions for the application.
 * Roles are assigned to users after OAuth authentication based on whitelisted
 * emails or Discord IDs.
 */

// Define role types
export enum RoleType {
  ADMIN = 'admin',
  MODERATOR = 'moderator',
  MYTHIC = 'mythic',
  PREMIUM = 'premium',
  USER = 'user',
  GUEST = 'guest'
}

// Define permission types
export enum Permission {
  // Administrative permissions
  MANAGE_USERS = 'manage:users',
  MANAGE_CONTENT = 'manage:content',
  MANAGE_SETTINGS = 'manage:settings',
  VIEW_ANALYTICS = 'view:analytics',
  
  // Content permissions
  CREATE_CAMPAIGN = 'create:campaign',
  EDIT_ANY_CAMPAIGN = 'edit:any:campaign',
  DELETE_ANY_CAMPAIGN = 'delete:any:campaign',
  
  // User permissions
  EDIT_OWN_CAMPAIGN = 'edit:own:campaign',
  DELETE_OWN_CAMPAIGN = 'delete:own:campaign',
  CREATE_CHARACTER = 'create:character',
}

// Role definitions with permissions
export const roles = {
  [RoleType.ADMIN]: {
    name: 'Administrator',
    description: 'Full access to all features and administrative functions',
    permissions: [
      Permission.MANAGE_USERS,
      Permission.MANAGE_CONTENT,
      Permission.MANAGE_SETTINGS,
      Permission.VIEW_ANALYTICS,
      Permission.CREATE_CAMPAIGN,
      Permission.EDIT_ANY_CAMPAIGN,
      Permission.DELETE_ANY_CAMPAIGN,
      Permission.EDIT_OWN_CAMPAIGN,
      Permission.DELETE_OWN_CAMPAIGN,
      Permission.CREATE_CHARACTER,
    ]
  },
  [RoleType.MODERATOR]: {
    name: 'Moderator',
    description: 'Can manage content but not users or settings',
    permissions: [
      Permission.MANAGE_CONTENT,
      Permission.VIEW_ANALYTICS,
      Permission.CREATE_CAMPAIGN,
      Permission.EDIT_ANY_CAMPAIGN,
      Permission.DELETE_ANY_CAMPAIGN,
      Permission.EDIT_OWN_CAMPAIGN,
      Permission.DELETE_OWN_CAMPAIGN,
      Permission.CREATE_CHARACTER,
    ]
  },
  [RoleType.MYTHIC]: {
    name: 'Mythic VIP',
    description: 'Verified VIP user (official creators, celebrities, verified contributors)',
    permissions: [
      Permission.CREATE_CAMPAIGN,
      Permission.EDIT_OWN_CAMPAIGN,
      Permission.DELETE_OWN_CAMPAIGN,
      Permission.CREATE_CHARACTER,
    ]
  },
  [RoleType.PREMIUM]: {
    name: 'Premium User',
    description: 'Access to premium features',
    permissions: [
      Permission.CREATE_CAMPAIGN,
      Permission.EDIT_OWN_CAMPAIGN,
      Permission.DELETE_OWN_CAMPAIGN,
      Permission.CREATE_CHARACTER,
    ]
  },
  [RoleType.USER]: {
    name: 'User',
    description: 'Standard user access',
    permissions: [
      Permission.EDIT_OWN_CAMPAIGN,
      Permission.DELETE_OWN_CAMPAIGN,
      Permission.CREATE_CHARACTER,
    ]
  },
  [RoleType.GUEST]: {
    name: 'Guest',
    description: 'Limited access',
    permissions: []
  }
};

// Admin/Moderator whitelist
// This defines which users should receive admin/moderator roles after OAuth login
export const roleWhitelist = {
  [RoleType.ADMIN]: {
    discordIds: process.env.REACT_APP_ADMIN_DISCORD_IDS?.split(',') || [],
    emails: process.env.REACT_APP_ADMIN_EMAILS?.split(',') || []
  },
  [RoleType.MODERATOR]: {
    discordIds: process.env.REACT_APP_MODERATOR_DISCORD_IDS?.split(',') || [],
    emails: process.env.REACT_APP_MODERATOR_EMAILS?.split(',') || []
  },
  // VIP verified users (Matt Mercer, official creators, etc.)
  [RoleType.MYTHIC]: {
    discordIds: process.env.REACT_APP_MYTHIC_DISCORD_IDS?.split(',') || [],
    emails: process.env.REACT_APP_MYTHIC_EMAILS?.split(',') || []
  },
  // Users to assign premium role
  [RoleType.PREMIUM]: {
    discordIds: [],
    emails: [
      'premium@mythbound.app',
    ]
  }
};

// Helper function to check if a user has a specific permission
export const hasPermission = (userPermissions: Permission[], permission: Permission): boolean => {
  return userPermissions.includes(permission);
};

// Helper to get permissions for a role
export const getPermissionsForRole = (roleType: RoleType): Permission[] => {
  return roles[roleType]?.permissions || [];
};

// Helper to get the highest role from an array of roles
export const getHighestRole = (userRoles: RoleType[]): RoleType => {
  const roleHierarchy = [
    RoleType.ADMIN,
    RoleType.MODERATOR,
    RoleType.MYTHIC,
    RoleType.PREMIUM,
    RoleType.USER,
    RoleType.GUEST
  ];
  
  for (const role of roleHierarchy) {
    if (userRoles.includes(role)) {
      return role;
    }
  }
  
  return RoleType.GUEST;
};

export default roles;